import { isTenantSmava } from 'utils/url'
import { useStores } from 'stores/utils/hooks/useStores'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { Experiments } from 'CustomOptimizelyProvider'
import { PossibleVariants } from 'utils/tracking/optimizely/types'
import useVoucherAmount from './useVoucherAmount'
import { VoucherBannerVariant } from '../VoucherBanner.types'

export interface UseVoucherBannerReturn {
  isVoucherBannerVisible: boolean
  shouldShowTopActivationPromptBanner: boolean
  shouldShowTopActivatedVoucherBanner: boolean
  shouldShowContactPageVoucherActivationBanner: boolean
  isVoucherActivated: boolean
  voucherBannerVariant: VoucherBannerVariant
  voucherAmount: number | null
}

/**
 * Manages voucher banner state and side effects for both Smava and FFG tenants.
 * Determines visibility flags and fires amount-tier tracking on mount.
 *
 * Smava: always eligible when loan amount qualifies.
 * FFG: eligible only when CAT_4521_FFG_AB_VOUCHER experiment is in variation_b.
 */
const useVoucherBanner = (): UseVoucherBannerReturn => {
  const {
    traversalSession,
    navigation: { currentPageInSession }
  } = useStores()
  const {
    rootStore: { experimentData }
  } = useRootStore()

  const voucherAmount = useVoucherAmount()

  const isSmava = isTenantSmava()
  const isFFG = !isSmava
  // @ts-ignore - experimentData type needs refinement for dynamic access
  const isFFGExperimentEnabled =
    // @ts-ignore
    experimentData[Experiments.CAT_4521_FFG_AB_VOUCHER] ===
    PossibleVariants.VARIATION_B

  const isEligible =
    voucherAmount !== null && (isSmava || (isFFG && isFFGExperimentEnabled))

  const voucherBannerVariant: VoucherBannerVariant = isSmava ? 'smava' : 'ffg'

  return {
    isVoucherBannerVisible: isEligible,
    shouldShowTopActivationPromptBanner:
      isEligible && currentPageInSession === 'contact',
    shouldShowTopActivatedVoucherBanner:
      isEligible &&
      currentPageInSession !== 'contact' &&
      traversalSession.isVoucherActivated,
    shouldShowContactPageVoucherActivationBanner: isEligible,
    isVoucherActivated: traversalSession.isVoucherActivated,
    voucherBannerVariant,
    voucherAmount
  }
}

export default useVoucherBanner
